import CentricDispatcher from '../dispatcher/CentricDispatcher.react';
import { ActionTypes } from '../constants/CentricConstants';
import { ReduceStore } from 'flux/utils';
import update from 'immutability-helper';
import moment from 'moment';

const sanitizeEntity = ({id, moduleId, capacity, attendeeCount, timeStart, ...event}) => ({
    id: Number(id),
    moduleId: Number(moduleId) || null,
    capacity: Number(capacity) || 0,
    attendeeCount: Number(attendeeCount) || 0,
    timeStart: timeStart && moment(timeStart),
    ...event,
    access: {
        id: Number(event.accessGroupId) || null,
        left: Number(event.accessLeft) || null,
        right: Number(event.accessRight) || null
    }
});

const normalise = (source, idField = 'id') => {
    let data = {};
    source.forEach((row) => {
        const id = row[idField];
        data[id] = row;
    });
    return data;
};

class ModuleEventStore extends ReduceStore {

    getInitialState() {
        return {isFetching: false, events: []};
    }

    isFetching() {
        return !!this.getState().isFetching;
    }

    getAll() {
        return this.getState().events;
    }

    getByModule(moduleId) {
        return this.getState().events.filter(event => event.moduleId === moduleId);
    }

    getEvent(moduleId, eventId) {
        return this.getState().events.find(event => event !== undefined && event.id === eventId && event.moduleId === moduleId);
    }

    getNextEventByModule(moduleId) {
        let id;
        let time;
        const now = Date.now();

        this._state.events.forEach((event, i) => {
            if (event.moduleId === moduleId) {
                const current = new Date(event.timeStart);
                if (current > now) {
                    if (!time || time > current) {
                        id = i;
                        time = current;
                    }
                }
            }
        });

        return (id ? {...this.getState().events[id]} : null);
    }

    getNextAvailableEventsByModule(moduleId, limit, status = null) {
        const now = Date.now();
        let mapped = [];
        this._state.events.forEach((event, index) => {
            const date = Date.parse(event.timeStart);
            if (event.moduleId === moduleId && date > now && event.capacity > event.attendeeCount && event.visibility === 'public' && (!status || event.status === status)) {
                mapped.push({index, value: date});
            }
        });
        mapped.sort((a,b) => a.value - b.value);
        const sorted = mapped.map((event) => this.getState().events[event.index]);
        sorted.splice(limit);
        return sorted;
    }

    getNextEventsByAttendee(moduleId, limit) {
        const now = Date.now();
        let mapped = [];
        this._state.events.forEach((event, index) => {
            const date = Date.parse(event.timeStart);
            if (event.moduleId === moduleId && date > now && !!event.attendanceStatus) {
                mapped.push({index, value: date});
            }
        });
        mapped.sort((a,b) => a.value - b.value);
        const sorted = mapped.map((event) => this.getState().events[event.index]);
        sorted.splice(limit);
        return sorted;
    }

    getIndex({id, moduleId}) {
        return this._state.events.findIndex(event => event !== undefined && id === event.id && moduleId === event.moduleId);
    }

    reduce(state, action) {
        let index, event, updates;
        switch (action.type) {
            case ActionTypes.MODULE_EVENT_LIST_REQUEST:
                return update(this.getState(), { isFetching: { $set: true }});

            case ActionTypes.MODULE_EVENT_LIST_SUCCESS:
                const data = normalise(action.data.map(sanitizeEntity));

                return update(this.getState(), {
                    isFetching: { $set: false },
                    events: { $merge: data }
                });

            case ActionTypes.MODULE_EVENT_LIST_FAILURE:
                return update(this.getState(), { isFetching: { $set: false }});

            case ActionTypes.MODULE_EVENT_INFO_REQUEST:
                return update(this.getState(), { isFetching: { $set: true }});

            case ActionTypes.MODULE_EVENT_INFO_SUCCESS:
                event = normalise([sanitizeEntity(action.data)]);
                index = this.getIndex(event);
                if (0 <= index) {
                    updates = { [index]: { $set: event } };
                } else {
                    updates = { $merge: event };
                }
                return update(this.getState(), {
                    isFetching: { $set: false },
                    events: updates
                });

            case ActionTypes.MODULE_EVENT_INFO_FAILURE:
                return update(this.getState(), { isFetching: { $set: false }});

            case ActionTypes.MODULE_EVENT_ADD_REQUEST:
                return update(this.getState(), { isFetching: { $set: true }});

            case ActionTypes.MODULE_EVENT_ADD_SUCCESS:
                return update(this.getState(), {
                    isFetching: { $set: false },
                    events: { $merge: normalise([sanitizeEntity(action.data)]) }
                });

            case ActionTypes.MODULE_EVENT_ADD_FAILURE:
                return update(this.getState(), { isFetching: { $set: false }});

            case ActionTypes.MODULE_EVENT_DELETE_REQUEST:
                return update(this.getState(), { isFetching: { $set: true }});

            case ActionTypes.MODULE_EVENT_DELETE_SUCCESS:
                index = this.getIndex(action.payload.event);
                return update(this.getState(), {
                    isFetching: { $set: false },
                    events: { $splice: [ [index, 1] ]}
                });

            case ActionTypes.MODULE_EVENT_DELETE_FAILURE:
                return update(this.getState(), { isFetching: { $set: false }});

            default:
                return state;
        }
    }
}

export default new ModuleEventStore(CentricDispatcher);
